import { FormsEntities } from '@bimser/common';
import {
	BCButton,
	BCLoading,
	BCNotification,
	BCScrollbars,
	BCSkeleton,
	ContentType,
	findMessage
} from '@bimser/components';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { createGuid, debounce, EventEmitter, FormItemTypes, IDictionary } from '@bimser/core';
import IconAdd from '@bimser/icons/16/add';
import IconDeleteDocument from '@bimser/icons/16/delete-document';
import { Map } from 'immutable';
import React from 'react';
import { Form } from '../..';
import { IDataSource, IOnLoadArgs, IPropertyField } from '../../../../entities/common';
import { getDataSourceData } from '../../../../helper';
import DataBoundControl from '../../base/DataBoundControl';
import FormView from '../FormView';
import { IFormViewer } from '../FormView/entities';
import { IFormDocumentData } from '../FormViewer/entities';
import { getControlData } from '../FormViewer/helpers';
import Styles from './assets/Styles.scss';
import { IRepeaterProperties } from './entities';
import cloneDeep from 'lodash/cloneDeep';

const infinityScrollContainerId = createGuid();

export default class Repeater extends DataBoundControl {
	projectId: string;
	projectName: string;
	formName: string;
	formId: string;
	viewName: string;
	height: number;
	formHeight: number;
	activeView: string;
	documentIds: Array<number>;
	documentsData: IFormDocumentData[];
	dataSource: IDataSource;
	documentIdsSourceColumn: string;
	_selectedStaticItemId: string = '';

	__repeaterFormViewerProps: IDictionary<IFormViewer> = {};
	__formViewerForms: IDictionary<Form> = {};
	__searchValue: string;
	__items: IDictionary<Map<string, LayoutCreatorEntities.LayoutDataItem>> = {};
	__documentsData: IFormDocumentData[];
	__fetchedDocumentsIds: number[] = [];
	__staticDocumentIds: string[] = [];
	__repeaterEventEmitter: EventEmitter;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Repeater;
		this.documentsData = [];
		this.documentIds = [];
		this.activeView = '';
		this.projectId = '';
		this.projectName = '';
		this.formName = '';
		this.formId = '';
		this.viewName = '';
		this.height = 450;
		this.formHeight = 450;
		this.documentIdsSourceColumn = '';
		this.dataSource = {
			fileId: '',
			displayExpr: [],
			valueExpr: null,
			name: '',
			runAtServer: false,
			parameters: [],
			connectionType: 1,
			useClientCachedData: true
		};

		this.__repeaterEventEmitter = new EventEmitter();

		this.__renderItem = this.__renderItem.bind(this);
		this.__setForm = this.__setForm.bind(this);
		this.__loadMoreData = this.__loadMoreData.bind(this);
		this.__addDocument = this.__addDocument.bind(this);
		this.__getTotalCount = this.__getTotalCount.bind(this);
		this.__onClickListItem = this.__onClickListItem.bind(this);
		this.__deleteDocument = this.__deleteDocument.bind(this);
		this.__showWarnNotifications = this.__showWarnNotifications.bind(this);
		this.__onScrollInfinityScroll = debounce(this.__onScrollInfinityScroll.bind(this), 200);
	}

	__loadProperties(data: IRepeaterProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.projectId = data.projectId;
		this.__proxyChanged.projectName = data.projectName;
		this.__proxyChanged.formName = data.formName;
		this.__proxyChanged.formId = data.formId;
		this.__proxyChanged.viewName = data.viewName;
		this.__proxyChanged.height = data.height;
		this.__proxyChanged.activeView = data.activeView;
		this.__proxyChanged.documentIds = data.documentIds;
		this.__proxyChanged.documentsData = data.documentsData;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.documentIdsSourceColumn = data.documentIdsSourceColumn;
		this.__proxyChanged.formHeight = data.formHeight;
		this.__proxyChanged._selectedStaticItemId = data._selectedStaticItemId;

		Object.keys(this.__formViewerForms).forEach((windowId: string) => {
			this.__formViewerForms[windowId]?.__createControls();
		});
	}

	__onLoad({ externalData, reload, forceRefresh, requiredError }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (this.__form.serviceUrl && this.dataSource.name) {
			this.__proxyChanging.loading = true;

			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				loadOptions: {
					pagination: {
						skip: 0,
						take: 100
					},
					sorts: null,
					filters: []
				},
				forceRefresh,
				requiredError,
				projectVersion: this.__form.packageVersion
			})
				.then(response => {
					if (response.data?.result?.length && this.documentIdsSourceColumn) {
						this.__proxyChanging.documentIds = response.data?.result?.map(
							data => data[this.documentIdsSourceColumn]
						);

						const mutateFormData = structure =>
							new Promise<any>((resolve, reject) => {
								this.emit('event:OnRowDataBound', cloneDeep(structure))
									.then(mutatedFormsData => {
										resolve(mutatedFormsData[0]);
									})
									.catch((err: any) => {
										resolve(structure);
									});
							});

						this.__form.applyChanges().then(() => {
							const isOnOrDataBoundExist = this.clientEvents.find(
								event => event.name === 'OnRowDataBound'
							);
							this.__fetchedDocumentsIds = cloneDeep(this.documentIds);
							const firstLoadCount =
								Number((this.height / this.formHeight).toFixed(0)) + 2;
							this.__fetchedDocumentsIds = this.__fetchedDocumentsIds.splice(
								0,
								firstLoadCount
							);
							this.__form.__getFormViewerData?.({
								serviceUrl: this.__form.serviceUrl,
								viewers: [
									{
										projectName: this.projectName,
										formName: this.formName,
										viewName: this.viewName,
										formId: this.__form.__windowId.split('_')[0],
										documentIds: this.__fetchedDocumentsIds,
										controlId: this.name,
										controlType: 'Repeater',
										eventCallback: isOnOrDataBoundExist ? mutateFormData : null
									}
								]
							});
						});
					}
				})
				.catch(() => {
					this.__proxyChanging.documentIds = [];
				})
				.finally(() => {
					this.__proxyChanging.loading = false;
				});
		} else if (this.projectName && this.formName && this.viewName) {
			this.__fetchedDocumentsIds = cloneDeep(this.documentIds);
			this.__fetchedDocumentsIds = this.__fetchedDocumentsIds.slice(0, 3);
			this.__form.__getFormViewerData?.({
				serviceUrl: this.__form.serviceUrl,
				viewers: [
					{
						projectName: this.projectName,
						formName: this.formName,
						viewName: this.viewName,
						formId: this.__form.__windowId.split('_')[0],
						documentIds: this.__fetchedDocumentsIds,
						controlId: this.name,
						controlType: FormItemTypes.EControlTypes.Repeater
					}
				]
			});
		}
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'projectId',
				type: 'string'
			},
			{
				name: 'formName',
				type: 'string'
			},
			{
				name: 'viewName',
				type: 'string'
			},
			{
				name: 'projectName',
				type: 'string'
			},
			{
				name: 'formId',
				type: 'string'
			},
			{
				name: 'height',
				type: 'string'
			},
			{
				name: 'documentIdsSourceColumn',
				type: 'string'
			},
			{
				name: 'formHeight',
				type: 'number'
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			projectId: this.projectId,
			projectName: this.formName,
			formName: this.viewName,
			viewName: this.projectName,
			formId: this.formId,
			height: this.height,
			documentIds: this.documentIds,
			documentsData: this.documentsData,
			dataSource: this.dataSource,
			documentIdsSourceColumn: this.documentIdsSourceColumn,
			formHeight: this.formHeight
		};
	}

	__updateFormDocumentData(
		items: Map<string, LayoutCreatorEntities.LayoutDataItem>,
		documentId: any
	) {
		if (JSON.stringify(this.__items?.[documentId] || {}) === JSON.stringify(items || {})) {
			return;
		}

		this.__items[documentId] = items;

		let controls = [];

		if (this.__staticDocumentIds.includes(documentId)) {
			const documentControls = this.documentsData?.find(
				documentData => (documentData as any).id === documentId
			)?.controls;
			controls = documentControls ? [...documentControls] : [];
		} else {
			const documentControls = this.documentsData?.find(
				documentData => documentData.documentId === documentId
			)?.controls;
			controls = documentControls ? [...documentControls] : [];
		}

		items.forEach(item => {
			const { text, value } = getControlData(item);
			const controlName = item.getIn(['properties', 'name']);
			const isExistControl = controls?.find(data => data.name === controlName);

			if (controlName && isExistControl) {
				controls = controls.map(data => {
					if (data.name === controlName) {
						return {
							...data,
							value,
							text
						};
					}

					return data;
				});
			} else if (controlName) {
				controls = [
					...(controls || []),
					{ name: controlName, text: text || '', value: value }
				];
			}
		});

		this.__form.applyChanges().then(() => {
			const _documentsData: IFormDocumentData[] = cloneDeep(this.documentsData);

			if (this.__staticDocumentIds.includes(documentId)) {
				if (!_documentsData.find(documentData => (documentData as any).id === documentId)) {
					_documentsData.push({ documentId: 0, ...{ id: documentId }, controls: [] });
				}

				_documentsData.find(
					documentData => (documentData as any).id === documentId
				).controls = controls;
				this.__proxyChanging.documentsData = _documentsData;
			} else {
				if (!_documentsData.find(documentData => documentData.documentId === documentId)) {
					_documentsData.push({ documentId, controls: [] });
				}

				_documentsData.find(
					documentData => documentData.documentId === documentId
				).controls = controls;
				this.__proxyChanging.documentsData = _documentsData;
			}
		});
	}

	__setForm(form: any, windowId: number) {
		this.__formViewerForms[windowId] = form;
		this.__formViewerForms[windowId].subscribe(
			'system:PropertyChanged',
			(data: FormsEntities.IPropertyChangeTransaction) => {
				this.emit('event:OnRepeatedFormPropertyChanged', data);
			}
		);
	}

	__renderItem(id: number | string): React.ReactElement {
		const repeaterProps = this.__repeaterFormViewerProps[id];
		if (repeaterProps) {
			const data = repeaterProps.data.setIn(
				[repeaterProps.formName, 'properties', 'readOnly'],
				this.__isReadOnly() || this.__isDisabled()
			);
			this.__updateFormDocumentData(data, id);

			const style: React.CSSProperties = { height: this.formHeight };

			return (
				<div
					className={Styles.repeaterContainer}
					style={style}
					key={id}
				>
					<BCScrollbars>
						<div
							className={`${this.name}_RepeaterContainer`}
							onClick={this.__onClickListItem}
							style={style}
							id={id.toString()}
							key={id}
						>
							<FormView
								{...repeaterProps}
								formViewerProps={{
									formViewerControlType: FormItemTypes.EControlTypes.Repeater,
									formViewerFormId: this.__form.__getWindowId().split('_')[0],
									formViewerViewName: this.viewName,
									formViewerControlName: this.name
								}}
								setForm={(form: Form) => {
									this.__setForm(form, id as number);
								}}
								eventEmitter={this.__repeaterEventEmitter}
								documentId={id as number}
								data={data}
							/>
						</div>
					</BCScrollbars>
				</div>
			);
		}

		return <div key={id}></div>;
	}

	__getListItems() {
		return [...this.__fetchedDocumentsIds, ...this.__staticDocumentIds];
	}

	__showWarnNotifications() {
		const getMessage = (id: string) =>
			findMessage.get('102835').replace('{0}', findMessage.get(id));

		if (!this.projectName) {
			BCNotification.warn({ message: getMessage('100407') });
			return true;
		}

		if (!this.formName) {
			BCNotification.warn({ message: getMessage('100178') });
			return true;
		}

		if (!this.viewName) {
			BCNotification.warn({ message: getMessage('100007') });
			return true;
		}
	}

	__addDocument() {
		if (this.__showWarnNotifications()) {
			return;
		}

		const id = createGuid();
		this.__staticDocumentIds = [...this.__staticDocumentIds, id];

		this.__form.__getFormViewerData?.({
			serviceUrl: this.__form.serviceUrl,
			viewers: [
				{
					projectName: this.projectName,
					formName: this.formName,
					viewName: this.viewName,
					formId: this.__form.__windowId.split('_')[0],
					documentIds: [],
					customDocumentId: id,
					controlId: this.name,
					controlType: FormItemTypes.EControlTypes.Repeater
				}
			]
		});
	}

	__getRepeaterHeader() {
		if (!this.dataSource?.name) {
			const isDisabled = this.__isDisabled() || this.__isReadOnly();

			return (
				<div style={{ display: 'flex' }}>
					<BCButton
						className={Styles.buttonStyles}
						icon={<IconAdd />}
						onClick={this.__addDocument}
						disabled={isDisabled}
					>
						{findMessage.get('100016')}
					</BCButton>
					<BCButton
						disabled={isDisabled || !this._selectedStaticItemId}
						onClick={this.__deleteDocument}
						className={Styles.buttonStyles}
						icon={<IconDeleteDocument />}
					>
						{findMessage.get('100013')}
					</BCButton>
				</div>
			);
		}

		return <div></div>;
	}

	__deleteDocument() {
		if (this.__showWarnNotifications()) {
			return;
		}

		this.__proxyChanging.documentIds = this.documentIds.filter(
			documentId => documentId.toString() !== this._selectedStaticItemId
		);
		this.__proxyChanging.documentsData = this.documentsData.filter(
			document => document.documentId.toString() !== this._selectedStaticItemId
		);

		this.__staticDocumentIds = this.__staticDocumentIds.filter(
			documentId => documentId.toString() !== this._selectedStaticItemId
		);
		this.__fetchedDocumentsIds = this.__fetchedDocumentsIds.filter(
			documentId => documentId.toString() !== this._selectedStaticItemId
		);

		this.__form.__closeFormById(
			this.__formViewerForms[this._selectedStaticItemId].__getWindowId()
		);
		this.__proxyChanging._selectedStaticItemId = '';
	}

	__getScrollHeight() {
		const minusValue = this.dataSource?.name ? 0 : 32;
		return `${this.height - minusValue}px`;
	}

	__loadMoreData() {
		let documentIds = cloneDeep(this.documentIds);
		const loadCount = Number((this.height / this.formHeight).toFixed(0)) + 2;
		documentIds = documentIds.filter(
			documentId => !this.__fetchedDocumentsIds.includes(documentId)
		);
		documentIds = documentIds.splice(0, loadCount);
		this.__fetchedDocumentsIds = [...this.__fetchedDocumentsIds, ...documentIds];

		this.__form.__getFormViewerData?.({
			serviceUrl: this.__form.serviceUrl,
			viewers: [
				{
					projectName: this.projectName,
					formName: this.formName,
					viewName: this.viewName,
					formId: this.__form.__windowId.split('_')[0],
					documentIds: documentIds,
					controlId: this.name,
					controlType: FormItemTypes.EControlTypes.Repeater
				}
			]
		});
	}

	__getTotalCount() {
		if (this.dataSource.name && this.documentIds.length) {
			return this.documentIds.length;
		}

		return this.__getListItems().length;
	}

	__onClickListItem(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		if (!this.dataSource.name) {
			const repeaterContainers = document.querySelectorAll<HTMLDivElement>(
				`.${this.name}_RepeaterContainer`
			);

			repeaterContainers.forEach(repeaterContainer => {
				repeaterContainer.style.borderStyle = 'unset';
				repeaterContainer.style.borderWidth = '0px';
				repeaterContainer.style.borderColor = 'unset';
				repeaterContainer.style.boxShadow = 'unset';
			});

			e.currentTarget.style.borderStyle = 'solid';
			e.currentTarget.style.borderWidth = '0.5px';
			e.currentTarget.style.borderColor = '#2495e0';
			e.currentTarget.style.outline = '0px';
			e.currentTarget.style.boxShadow = 'rgba(0, 120, 212, 0.2)';

			this.__proxyChanging._selectedStaticItemId = e.currentTarget.id;
		}
	}

	__onScrollInfinityScroll(event: any) {
		const scrollElement = event.target;
		const isCloseToBottom =
			Math.abs(scrollElement.scrollHeight - this.height - scrollElement.scrollTop) <= 20;
		const isLoadAllData = this.__getListItems().length === this.__getTotalCount();

		if (isCloseToBottom && !isLoadAllData) {
			this.__loadMoreData();
		}
	}

	__render(children?: React.ReactNode) {
		const items = this.__getListItems();
		const totalCount = this.__getTotalCount();

		return super.__render(
			<BCLoading show={this.loading}>
				<div
					className={Styles.listContainerStyles}
					style={{ height: this.height }}
				>
					<BCScrollbars
						styles={{ height: this.__getScrollHeight() }}
						onScroll={this.__onScrollInfinityScroll}
					>
						{!this.dataSource?.name && this.__getRepeaterHeader()}
						{items.map(i => this.__renderItem(i))}
						{items.length !== totalCount ? (
							<BCSkeleton
								contentType={ContentType.OneRow}
								loadingStatus
							/>
						) : null}
						<div id={`repeaterFooter_${this.name}`}></div>
					</BCScrollbars>
				</div>
			</BCLoading>
		);
	}
}
